import { isAssessmentSlideType } from "@/features/tutor/constants/assessmentTypes";
import type { PracticeDiagramState } from "@/components/practiceDiagram/practiceDiagramLoader";

export interface LessonRow {
  index: number;
  name: string;
  slide_type?: string | null;
}

export const orderLessonRows = <T extends { index: number }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => a.index - b.index);

export const biteNumbersByIndex = (
  orderedRows: LessonRow[],
): Record<number, number> => {
  const numbers: Record<number, number> = {};
  orderedRows
    .filter((row) => !isAssessmentSlideType(row.slide_type))
    .forEach((row, position) => {
      numbers[row.index] = position + 1;
    });
  return numbers;
};

export type PreviewFetchStatus = "loading" | "loaded" | "error";

export type RowPreviewState = "ready" | "loading" | "error" | "empty";

export const rowPreviewState = (
  hasContent: boolean,
  status: PreviewFetchStatus,
): RowPreviewState => {
  if (hasContent) return "ready";
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  return "empty";
};

export const formatLessonUpdatedAt = (value?: string): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * A practice row's preview state: the lesson's slide list has to arrive before
 * the host url is known, then the host diagram itself has to load.
 */
export const practicePreviewState = (
  sourceStatus: PreviewFetchStatus,
  diagramStatus: PracticeDiagramState["status"],
): RowPreviewState => {
  if (sourceStatus !== "loaded") return sourceStatus;
  if (diagramStatus === "missing") return "empty";
  return diagramStatus;
};
